// BookingFlow.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import axios from "axios";
import BookingSummary from "./BookingSummary";
import PageLayout from "./PageLayout";
import DistanceForm from "./DistanceForm";
import VehicleSelection from "./VehicleSelection";
import PersonalDetails from "./PersonalDetails";
import PaymentMethod from "./PaymentMethod";
import Confirmation from "./Confirmation";

function BookingFlow() {
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [addons, setAddons] = useState({
    babySeat: false,
    wineBottle: false,
    redCarpet: false,
    babySeatDetails: {
      infant: { selected: false, quantity: 0, price: 10 },
      child: { selected: false, quantity: 0, price: 15 },
      booster: { selected: false, quantity: 0, price: 20 },
      totalBabySeatPrice: 0,
    },
    wineBottlePrice: 20,
    redCarpetPrice: 30,
  });
  const [totalPrice, setTotalPrice] = useState(0);
  const [personalDetails, setPersonalDetails] = useState({
    name: "",
    email: "",
    phone: "",
    extraNotes: "",
    numberOfPassengers: 1,
    numberOfLuggage: 1,
    flightNumber: "",
    returnNumberOfPassengers: 1,
    returnNumberOfLuggage: 1,
  });
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [stops, setStops] = useState([]);
  const [stopFee, setStopFee] = useState(0);
  const [showStops, setShowStops] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isReturnBooking, setIsReturnBooking] = useState(false);
  const [returnDistance, setReturnDistance] = useState(null);
  const [returnDuration, setReturnDuration] = useState(null);
  const [returnVehicles, setReturnVehicles] = useState([]);
  const [returnSelectedVehicle, setReturnSelectedVehicle] = useState(null);
  const [returnAddons, setReturnAddons] = useState({
    babySeat: false,
    wineBottle: false,
    redCarpet: false,
    babySeatDetails: {
      infant: { selected: false, quantity: 0, price: 10 },
      child: { selected: false, quantity: 0, price: 15 },
      booster: { selected: false, quantity: 0, price: 20 },
      totalBabySeatPrice: 0,
    },
    wineBottlePrice: 20,
    redCarpetPrice: 30,
  });
  const [returnTotalPrice, setReturnTotalPrice] = useState(0);
  const [returnPickupLocation, setReturnPickupLocation] = useState("");
  const [returnDropoffLocation, setReturnDropoffLocation] = useState("");
  const [returnStops, setReturnStops] = useState([]);
  const [returnStopFee, setReturnStopFee] = useState(0);
  const [bookingType, setBookingType] = useState("one-way");
  const [user, setUser] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [ipAddress, setIpAddress] = useState(null);
  const [ipBlocked, setIpBlocked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-ip`);
        setIpAddress(response.data.ip);
      } catch {}
    };
    fetchIpAddress();
  }, []);

  useEffect(() => {
    if (selectedVehicle) {
      const basePrice = selectedVehicle.calculatedPrice + stopFee;
      const babySeatPrice = addons.babySeatDetails?.totalBabySeatPrice || 0;
      const wineBottlePrice = addons.wineBottle ? addons.wineBottlePrice : 0;
      const redCarpetPrice = addons.redCarpet ? addons.redCarpetPrice : 0;
      const addonsPrice = babySeatPrice + wineBottlePrice + redCarpetPrice;
      setTotalPrice(basePrice + addonsPrice);
    }
  }, [selectedVehicle, addons, stopFee]);

  useEffect(() => {
    if (returnSelectedVehicle) {
      const basePrice = returnSelectedVehicle.calculatedPrice + returnStopFee;
      const babySeatPrice = returnAddons.babySeatDetails?.totalBabySeatPrice || 0;
      const wineBottlePrice = returnAddons.wineBottle ? returnAddons.wineBottlePrice : 0;
      const redCarpetPrice = returnAddons.redCarpet ? returnAddons.redCarpetPrice : 0;
      const addonsPrice = babySeatPrice + wineBottlePrice + redCarpetPrice;
      setReturnTotalPrice(basePrice + addonsPrice);
    }
  }, [returnSelectedVehicle, returnAddons, returnStopFee]);

  const resetAddons = (isReturn = false) => {
    const defaultAddons = {
      babySeat: false,
      wineBottle: false,
      redCarpet: false,
      babySeatDetails: {
        infant: { selected: false, quantity: 0, price: 10 },
        child: { selected: false, quantity: 0, price: 15 },
        booster: { selected: false, quantity: 0, price: 20 },
        totalBabySeatPrice: 0,
      },
      wineBottlePrice: 20,
      redCarpetPrice: 30,
    };
    if (isReturn) setReturnAddons(defaultAddons);
    else setAddons(defaultAddons);
  };

  const handleVehicleSelect = (vehicle, isReturn = false) => {
    if (isReturn) setReturnSelectedVehicle(vehicle);
    else setSelectedVehicle(vehicle);
    resetAddons(isReturn);
  };

  const fetchVehicles = async (
    distanceKm,
    isReturn = false,
    pickupLat,
    pickupLon,
    dropoffLat,
    dropoffLon,
    pickupDate,
    pickupTime
  ) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/get-vehicles`,
        {
          distanceKm,
          pickupLat,
          pickupLon,
          dropoffLat,
          dropoffLon,
          pickupDate,
          pickupTime,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid vehicles data received");
      }
      if (isReturn) setReturnVehicles(response.data);
      else setVehicles(response.data);
    } catch (error) {
      if (error.response?.status === 429) {
        setIpBlocked(true);
        alert(error.response.data.error);
        navigate("/");
      } else {
        alert(`Failed to load vehicles: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOutboundDistanceCalculated = async (
    pickup,
    dropoff,
    stopsArr = [],
    pickupDate,
    pickupTime,
    pickupLat,
    pickupLon,
    dropoffLat,
    dropoffLon
  ) => {
    setLoading(true);
    setPickupLocation(pickup);
    setDropoffLocation(dropoff);
    setStops(stopsArr);
    setPersonalDetails((prev) => ({
      ...prev,
      pickupDate,
      pickupTime,
    }));
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/get-distance`, {
        origin: pickup,
        destination: dropoff,
        stops: stopsArr,
      });
      if (response.status === 429) {
        setIpBlocked(true);
        throw new Error(response.data.error);
      }
      if (response.data) {
        const { distanceKm, stopFee: sf, duration: dur } = response.data;
        setDistance(distanceKm);
        setStopFee(sf);
        setDuration(dur);
        fetchVehicles(
          distanceKm,
          false,
          pickupLat,
          pickupLon,
          dropoffLat,
          dropoffLon,
          pickupDate,
          pickupTime
        );
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setIpBlocked(true);
        alert(error.response.data.error);
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
    navigate("/booking/vehicle-selection");
  };

  const handleReturnDistanceCalculated = async (
    pickup,
    dropoff,
    stopsArr = [],
    pickupDate,
    pickupTime,
    pickupLat,
    pickupLon,
    dropoffLat,
    dropoffLon
  ) => {
    setLoading(true);
    setReturnPickupLocation(pickup);
    setReturnDropoffLocation(dropoff);
    setReturnStops(stopsArr);
    setPersonalDetails((prev) => ({
      ...prev,
      returnPickupDate: pickupDate,
      returnPickupTime: pickupTime,
    }));
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/get-return-distance`, {
        origin: pickup,
        destination: dropoff,
        stops: stopsArr,
      });
      if (response.status === 429) {
        setIpBlocked(true);
        throw new Error(response.data.error);
      }
      if (response.data) {
        const { distanceKm, stopFee: sf, duration: dur } = response.data;
        setReturnDistance(distanceKm);
        setReturnStopFee(sf);
        setReturnDuration(dur);
        fetchVehicles(
          distanceKm,
          true,
          pickupLat,
          pickupLon,
          dropoffLat,
          dropoffLon,
          pickupDate,
          pickupTime
        );
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setIpBlocked(true);
        alert(error.response.data.error);
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
    navigate("/booking/return-vehicle-selection");
  };

  const resetReturnBookingData = () => {
    setIsReturnBooking(false);
    setReturnDistance(null);
    setReturnDuration(null);
    setReturnVehicles([]);
    setReturnSelectedVehicle(null);
    setReturnAddons({
      babySeat: false,
      wineBottle: false,
      redCarpet: false,
      babySeatDetails: {
        infant: { selected: false, quantity: 0, price: 10 },
        child: { selected: false, quantity: 0, price: 15 },
        booster: { selected: false, quantity: 0, price: 20 },
        totalBabySeatPrice: 0,
      },
      wineBottlePrice: 20,
      redCarpetPrice: 30,
    });
    setReturnTotalPrice(0);
    setReturnPickupLocation("");
    setReturnDropoffLocation("");
    setReturnStops([]);
    setReturnStopFee(0);
  };

  const handleBackToOneWayBooking = () => {
    resetReturnBookingData();
    setBookingType("one-way");
    resetAddons(false);
    navigate("/booking/vehicle-selection");
  };

  const handleBookingConfirmation = async (paymentData) => {
    if (ipBlocked) {
      alert("Your IP is currently blocked. Please try again later.");
      navigate("/");
      return;
    }
    setLoading(true);
    try {
      const bookingData = {
        vehicleName: selectedVehicle.Vehicle_Name,
        pickupLocation,
        dropoffLocation,
        stops,
        stopFee,
        bookingStatus: "pending",
        customerName: personalDetails.name,
        customerEmail: personalDetails.email,
        customerPhone: personalDetails.phone,
        totalAmount: totalPrice + (isReturnBooking ? returnTotalPrice : 0) - couponDiscount,
        addons,
        pickupDate: new Date(personalDetails.pickupDate).toISOString().split("T")[0],
        pickupTime: personalDetails.pickupTime,
        notes: personalDetails.extraNotes,
        numberOfPassengers: personalDetails.numberOfPassengers,
        numberOfLuggage: personalDetails.numberOfLuggage,
        flightNumber: personalDetails.flightNumber,
        isReturnBooking,
        returnPickupLocation,
        returnDropoffLocation,
        returnStops,
        returnPickupDate:
          isReturnBooking && personalDetails.returnPickupDate
            ? new Date(personalDetails.returnPickupDate).toISOString().split("T")[0]
            : null,
        returnPickupTime: isReturnBooking ? personalDetails.returnPickupTime : null,
        returnNumberOfPassengers: isReturnBooking ? personalDetails.returnNumberOfPassengers : null,
        returnNumberOfLuggage: isReturnBooking ? personalDetails.returnNumberOfLuggage : null,
        returnDistanceKm: returnDistance,
        returnStopFee,
        returnTotalCost: returnTotalPrice,
        onewayTotalCost: totalPrice,
        returnVehicleName: returnSelectedVehicle?.Vehicle_Name,
        returnAddons,
        paymentMethod: paymentData.paymentMethod,
        paymentDetails: paymentData.paymentDetails || null,
        paymentStatus: paymentData.paymentMethod === "cash" ? "Pending" : "Completed",
        DistanceKm: distance,
        duration,
        returnDuration,
        couponCode,
        couponDiscount,
        basePrice:
          selectedVehicle.calculatedPrice -
          selectedVehicle.overcharge -
          selectedVehicle.eventAdditionalPrice -
          (selectedVehicle.lateNightChargeAmount || 0),
        overcharge: selectedVehicle.overcharge,
        eventAdditionalPrice: selectedVehicle.eventAdditionalPrice,
        lateNightCharge: selectedVehicle.lateNightChargeAmount || 0,
        returnBasePrice: returnSelectedVehicle
          ? returnSelectedVehicle.calculatedPrice -
            returnSelectedVehicle.overcharge -
            returnSelectedVehicle.eventAdditionalPrice -
            (returnSelectedVehicle.lateNightChargeAmount || 0)
          : 0,
        returnOvercharge: returnSelectedVehicle ? returnSelectedVehicle.overcharge : 0,
        returnEventAdditionalPrice: returnSelectedVehicle ? returnSelectedVehicle.eventAdditionalPrice : 0,
        returnLateNightCharge: returnSelectedVehicle ? returnSelectedVehicle.lateNightChargeAmount || 0 : 0,
        userId: user?._id || null,
        ipAddress,
      };
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/store-booking`, bookingData);
      if (response.data.success) {
        if (ipAddress) {
          try {
            await axios.post(`${process.env.REACT_APP_API_URL}/reset-ip-counter`, { ipAddress });
          } catch {}
        }
        setBookingDetails({
          ...bookingData,
          _id: response.data.bookingId,
          invoice: response.data.invoiceUrl,
          createdAt: new Date().toISOString(),
        });
        navigate("/booking/confirmation");
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setIpBlocked(true);
        alert(error.response.data.error);
        navigate("/");
      } else {
        alert("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReturnBooking = () => {
    setIsReturnBooking(true);
    setBookingType("return");
    navigate("/booking/return-trip");
  };

  const handleVehicleSelectionNext = () => {
    if (isReturnBooking) {
      navigate("/booking/return-trip");
    } else {
      navigate("/booking/personal-details");
    }
  };

  const handleReturnVehicleSelectionNext = () => {
    navigate("/booking/personal-details");
  };

  const handlePersonalDetailsNext = () => {
    navigate("/booking/payment");
  };

  const handleBack = () => {
    const path = location.pathname;

    // From confirmation: go home
    if (path.includes("/booking/confirmation")) {
      navigate("/");
      return;
    }

    // FIXED: From payment → go to Personal Details (do NOT jump to vehicle selection)
    if (path.includes("/booking/payment")) {
      navigate("/booking/personal-details");
      return;
    }

    // From personal-details → go to correct vehicle-selection (return or one-way)
    if (path.includes("/booking/personal-details")) {
      if (isReturnBooking) navigate("/booking/return-vehicle-selection");
      else navigate("/booking/vehicle-selection");
      return;
    }

    // From return-vehicle-selection → go to return-trip details form
    if (path.includes("/booking/return-vehicle-selection")) {
      navigate("/booking/return-trip");
      return;
    }

    // From return-trip → go back to outbound vehicle selection
    if (path.includes("/booking/return-trip")) {
      navigate("/booking/vehicle-selection");
      return;
    }

    // From vehicle-selection → go to initial distance form
    if (path.includes("/booking/vehicle-selection")) {
      navigate("/booking");
      return;
    }

    // Default fallback
    navigate("/");
  };

  const contextValue = {
    distance,
    duration,
    vehicles,
    selectedVehicle,
    addons,
    setAddons,
    totalPrice,
    personalDetails,
    setPersonalDetails,
    pickupLocation,
    dropoffLocation,
    stops,
    stopFee,
    showStops,
    setShowStops,
    paymentMethod,
    setPaymentMethod,
    couponCode,
    setCouponCode,
    couponDiscount,
    setCouponDiscount,
    loading,
    setLoading,
    isReturnBooking,
    returnDistance,
    returnDuration,
    returnVehicles,
    returnSelectedVehicle,
    returnAddons,
    setReturnAddons,
    returnTotalPrice,
    returnPickupLocation,
    returnDropoffLocation,
    returnStops,
    returnStopFee,
    bookingType,
    user,
    setUser,
    bookingDetails,
    handleVehicleSelect,
    handleOutboundDistanceCalculated,
    handleReturnDistanceCalculated,
    handleBackToOneWayBooking,
    handleBookingConfirmation,
    handleReturnBooking,
    handleVehicleSelectionNext,
    handleReturnVehicleSelectionNext,
    handlePersonalDetailsNext,
    resetAddons,
    handleBack
  };

  return (
    <PageLayout>
      <Routes>
        <Route index element={<DistanceForm context={contextValue} />} />
        <Route path="vehicle-selection" element={<VehicleSelection context={contextValue} />} />
        <Route path="personal-details" element={<PersonalDetails context={contextValue} />} />
        <Route path="payment" element={<PaymentMethod context={contextValue} />} />
        <Route path="confirmation" element={<Confirmation context={contextValue} />} />
        <Route path="return-trip" element={<DistanceForm isReturnBooking context={contextValue} />} />
        <Route path="return-vehicle-selection" element={<VehicleSelection isReturnBooking context={contextValue} />} />
      </Routes>
      {location.pathname !== "/booking" && location.pathname !== "/booking/confirmation" && (
        <BookingSummary
          pickupLocation={pickupLocation}
          dropoffLocation={dropoffLocation}
          stops={stops}
          selectedVehicle={selectedVehicle}
          addons={addons}
          totalPrice={totalPrice}
          isReturnBooking={isReturnBooking}
          returnPickupLocation={returnPickupLocation}
          returnDropoffLocation={returnDropoffLocation}
          returnStops={returnStops}
          returnSelectedVehicle={returnSelectedVehicle}
          returnAddons={returnAddons}
          returnTotalPrice={returnTotalPrice}
          step={location.pathname}
          couponCode={couponCode}
          couponDiscount={couponDiscount}
        />
      )}
    </PageLayout>
  );
}

export default BookingFlow;
